import KeenSlider from 'keen-slider';

if (!customElements.get('ia-slider')) {

  class IASlider extends HTMLElement {
    constructor() {
      super();
      this.slider = null;
      this.currentSlideElement = null;
      this.prevButton = null;
      this.nextButton = null;
      this.progressFill = null;
      this.paginationBullets = null;
    }
  
    connectedCallback() {
      if (this.dataset.deferInit === 'true') {
        return;
      }
      this.initializeSlideshow();
    }
  
    disconnectedCallback() {
      if (this.slider) {
        this.slider.destroy();
      }
    }
  
    initializeSlideshow() {
      const carousel = this.querySelector('.ia-slider__slides');
      this.currentSlideElement = this.querySelector('.js-ia-slider-current');
      this.prevButton = this.querySelector('.js-ia-slider-prev');
      this.nextButton = this.querySelector('.js-ia-slider-next');
      this.progressFill = this.querySelector('.ia-slider__progress-fill');
      this.dynamicBackgroundColor = this.closest('.ia-dynamic-background');
  
      if (!carousel) return;

      const shouldAutoplay = this.dataset.autoplay === 'true';
      const autoplaySeconds = parseInt(this.dataset.autoplaySeconds || '5', 10);
      const autoplayDelay = autoplaySeconds * 1000;
    
      // Autoplay plugin
      const createAutoplay = (delay) => (slider) => {
        let timeout;
        let mouseOver = false;
    
        const clearNextTimeout = () => clearTimeout(timeout);
    
        const nextTimeout = () => {
          clearTimeout(timeout);
          if (mouseOver) return;
          timeout = setTimeout(() => slider.next(), delay);
        };
    
        slider.on('created', () => {
          slider.container.addEventListener('mouseover', () => {
            mouseOver = true;
            clearNextTimeout();
          });
          slider.container.addEventListener('mouseout', () => {
            mouseOver = false;
            nextTimeout();
          });
          nextTimeout();
        });
    
        slider.on('dragStarted', clearNextTimeout);
        slider.on('animationEnded', nextTimeout);
        slider.on('updated', nextTimeout);
      };
    
      const plugins = [];
    
      if (shouldAutoplay) {
        plugins.push(createAutoplay(autoplayDelay));
      }

      const styles = getComputedStyle(this);
      const perViewTablet = (
        styles.getPropertyValue('--items-tablet').trim() ||
        styles.getPropertyValue('--items-mobile').trim()
      );
        
      this.slider = new KeenSlider(carousel, {
        loop: this.dataset.loop === 'true',
        slides: {
          perView: this.style.getPropertyValue('--items-desktop'),
          spacing: this.style.getPropertyValue('--spacing-desktop').replace('px', ''),
          origin: this.dataset.centeredDesktop === 'true' ? 'center' : undefined,
        },
        breakpoints: {
          '(max-width: 1023px)': {
            slides: {
              perView: this.style.getPropertyValue('--items-tablet') || this.style.getPropertyValue('--items-mobile'),
              spacing: this.style.getPropertyValue('--spacing-mobile').replace('px', ''),
              origin: this.dataset.centeredMobile === 'true' ? 'center' : undefined,
            },
          },
          ' (max-width: 640px)': {
            slides: {
              perView: this.style.getPropertyValue('--items-mobile'),
              spacing: this.style.getPropertyValue('--spacing-mobile').replace('px', ''),
              origin: this.dataset.centeredMobile === 'true' ? 'center' : undefined,
            },
          }
        },
        created: (slider) => {
          this.classList.add('is-initialized');
          const firstSlide = this.querySelector('.ia-slider__slide');
          if (firstSlide) {
            firstSlide.classList.add('is-active');
          }
          this.createProgressBar();
          this.createPagination();
          
          // Update progress after slider is created
          setTimeout(() => {
            this.updateProgress();
            this.updatePagination();
          }, 50);
        },
        slideChanged: (slider) => {
          this.updateProgress();
          this.updatePagination();
        }
      }, plugins);
  
      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => {
          console.log('prev button clicked');
          this.slider.prev();
        });
      }
  
      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => {
          console.log('next button clicked');
          this.slider.next();
        });
      }
  
      this.slider.on('slideChanged', (slide) => {
        const allSlides = this.querySelectorAll('.ia-slider__slide');
        allSlides.forEach(slide => slide.classList.remove('is-active'));
        
        const currentSlide = this.slider.track.details.rel;
        if (allSlides[currentSlide]) {
          allSlides[currentSlide].classList.add('is-active');
        }
        
        if (this.slider?.track?.details?.rel >= 0 && this.currentSlideElement) {
          this.currentSlideElement.textContent = this.slider.track.details.rel + 1;
        }
        
        if (this.dynamicBackgroundColor) {
          let productColor = '';
          if (allSlides[currentSlide]) {
            productColor = allSlides[currentSlide].dataset.productColor;
          }
          if (productColor) {
            this.dynamicBackgroundColor.style.setProperty('--ia-dynamic-background-color', productColor);
          }
        }

        if (this.paginationBullets) {
          this.paginationBullets.forEach((bullet, index) => {
            if (index === currentSlide) {
              bullet.classList.add('is-active');
            } else {
              bullet.classList.remove('is-active');
            }
          });
        }

        this.updateProgress();

        this.dispatchEvent(
          new CustomEvent('ia:slider:changed', {
            detail: {
              index: currentSlide,
              absoluteIndex: this.slider.track.details.abs,
              slidesCount: this.slider.track.details.slides.length
            },
            bubbles: true
          })
        );
      });

    }

    createPagination() {
      if (this.dataset.showPagination !== 'true') return;
    
      if (this.querySelector('.ia-slider__pagination')) return;
    
      const slides = this.querySelectorAll('.ia-slider__slide');
      const totalSlides = slides.length;
      if (!totalSlides) return;
    
      const container = document.createElement('div');
      container.className = 'ia-slider__pagination';
    
      const bullets = [];
    
      for (let i = 0; i < totalSlides; i++) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'ia-slider__pagination-bullet ia-iconic-button';
        if (i === 0) btn.classList.add('is-active');
        btn.setAttribute('aria-label', `Go to slide ${i + 1}`);
        btn.dataset.index = i;
    
        btn.innerHTML = `
          <svg width="26" height="27" viewBox="0 0 26 27" fill="none">
          <path d="M15.7123 8.04119C15.7897 8.02502 15.8938 8.02704 15.9721 7.98358C16.8834 7.4843 17.7688 6.94459 18.6997 6.47259C19.2543 6.19162 19.78 5.85405 20.3799 5.65697C21.0562 5.43461 21.7097 5.11119 22.4581 5.21226C22.4972 5.21732 22.5478 5.2254 22.5756 5.20721C23.3116 4.73421 23.8786 5.29716 24.5032 5.50536C24.6517 5.55489 24.8269 5.55792 24.9527 5.63777C25.4568 5.95613 25.6722 6.36546 25.8206 7.04162C25.8815 7.31753 26.1093 7.53281 25.8794 7.87139C25.8175 7.96134 26.035 8.32014 25.8495 8.5809C25.8093 8.6375 25.9474 8.81538 26 8.92959C25.8897 9.09029 25.7279 9.23886 25.6753 9.41876C25.5753 9.76038 25.3702 9.97464 25.129 10.2374C24.83 10.5619 24.5806 10.8772 24.1507 11.0197C24.0827 11.5624 23.5776 11.5907 23.2147 11.7929C22.8817 11.9788 22.7137 12.3599 22.2828 12.4579C21.9169 12.5408 21.5839 12.7662 21.0974 12.9936C21.5592 13.2351 21.9159 13.3888 22.2364 13.597C22.7333 13.9194 23.2322 14.2489 23.6806 14.6299C24.0878 14.9766 24.4898 15.3637 24.8073 15.773C25.2207 16.3056 25.5537 16.9555 25.7134 17.6033C25.8516 18.1653 26.0144 18.8273 25.5671 19.3882C25.5464 19.4145 25.5485 19.465 25.5516 19.5024C25.6454 20.5182 24.8197 20.7941 24.1424 21.1903C24.059 21.2388 23.9724 21.2833 23.8889 21.3338C22.4272 22.2293 21.0077 21.9887 19.6099 21.1812C19.3718 21.0437 19.1368 20.8992 18.8265 20.7143C18.8265 21.072 18.7574 21.4147 18.843 21.7138C18.9656 22.1454 18.9708 22.5689 18.9594 23.0035C18.9522 23.2622 18.9192 23.526 18.9584 23.7787C18.9976 24.0283 19.0027 24.2305 18.8461 24.4579C18.5378 24.9036 18.3028 25.3998 17.9853 25.8375C17.4678 26.552 16.7607 26.8653 15.8422 26.9765C14.7145 27.114 13.8146 26.6187 12.8549 26.264C12.6003 26.17 12.3848 25.89 12.223 25.6495C11.7117 24.8854 11.0881 24.1819 11.1149 23.1581C11.1303 22.577 10.9417 21.9928 10.8644 21.4066C10.8376 21.2034 10.8685 20.9922 10.8798 20.785C10.887 20.6516 10.9056 20.5182 10.919 20.3848C10.8798 20.3767 10.8417 20.3676 10.8025 20.3595C10.5386 20.8143 10.2397 21.254 10.0284 21.731C9.93868 21.9342 9.95311 22.1312 9.65314 22.0453C9.57789 22.6224 8.98001 22.8448 8.75014 23.3239C8.62232 23.5897 8.46047 23.8888 8.22544 24.0455C7.66674 24.4164 7.16472 24.85 6.5019 25.1138C5.90299 25.3523 5.29584 25.2816 4.74847 25.4595C3.7197 25.795 2.90844 25.3058 2.06625 24.9743C1.2251 24.6438 0.755041 23.9697 0.479811 23.0894C-0.0222022 21.4884 0.451979 20.1149 1.36941 18.8485C1.75701 18.3139 2.33427 17.9116 2.81773 17.4426C3.26614 17.008 3.81041 16.7129 4.33304 16.3814C4.74022 16.1237 5.09895 15.7922 5.59271 15.4041C5.34325 15.396 5.15152 15.4465 5.04122 15.3758C4.59693 15.0928 4.07224 15.1393 3.60219 14.9897C2.33427 14.5854 1.17253 13.9729 0.484963 12.8096C0.114897 12.183 -0.172704 11.4614 0.121082 10.6811C0.233442 10.382 0.263336 10.0444 0.414868 9.76846C0.786997 9.09231 1.36117 8.54249 2.08996 8.33227C2.89504 8.10082 3.73207 7.80166 4.62373 8.11699C4.89072 8.21099 5.27006 8.20795 5.52777 8.17359C5.99577 8.11194 6.36274 8.19886 6.75961 8.38583C7.55747 8.76181 8.36667 9.11454 9.18412 9.48244C9.09237 9.02661 9.03567 8.50105 8.8759 8.00784C8.64808 7.30136 8.63056 6.58984 8.64912 5.86416C8.66252 5.32344 8.61407 4.77969 8.65839 4.24301C8.76251 2.99582 9.02125 1.79006 9.95414 0.845064C10.1067 0.690428 10.317 0.554995 10.5263 0.495364C11.1664 0.311418 11.8117 0.100185 12.4694 0.0253933C13.5682 -0.0989218 14.0342 0.223489 14.6434 1.2079C14.9351 1.67888 15.3546 2.07103 15.4196 2.66027C15.4938 3.33137 15.5897 4.00044 15.6701 4.67154C15.6928 4.86054 15.7062 5.05257 15.7072 5.24359C15.7144 6.16838 15.7123 7.09215 15.7123 8.04119ZM15.4691 15.3576C15.6794 15.3879 15.9206 15.3727 16.1154 15.4597C16.7349 15.7366 17.2204 16.1328 17.4111 16.8342C17.5142 17.2152 17.7194 17.5862 17.9492 17.9136C18.511 18.7111 19.445 18.9658 20.2768 19.365C20.5541 19.4984 20.8273 19.6389 21.1036 19.7713C21.217 19.8259 21.3355 19.9077 21.452 19.9087C21.8922 19.9118 22.355 19.9643 22.7663 19.8491C23.1271 19.748 23.5837 19.7339 23.794 19.2841C24.0785 18.6747 23.8466 17.5306 23.3487 17.0727C22.457 16.2521 21.4778 15.5476 20.4356 14.9291C20.0521 14.7016 19.681 14.4187 19.1986 14.4116C18.7296 14.4045 18.2595 14.3813 17.7915 14.4035C17.306 14.4267 17.0813 14.0477 16.8627 13.7728C16.6906 13.5565 16.6988 13.1927 16.9535 12.8834C17.2112 12.5701 17.5761 12.5923 17.8853 12.4569C18.7017 12.1001 19.5388 11.7858 20.3511 11.4199C21.1366 11.0652 21.9921 10.8166 22.5725 10.1161C22.6818 9.98475 22.856 9.90895 22.9869 9.79272C23.5538 9.28838 24.093 8.79517 24.0115 7.91789C23.9559 7.31855 24.0095 7.30743 23.359 7.18715C23.0539 7.13056 22.7312 6.92337 22.4591 6.97996C21.6025 7.15886 20.751 7.38727 19.9274 7.67936C19.2759 7.91081 18.6584 8.24535 18.0472 8.5718C17.5297 8.84772 17.038 9.17215 16.5453 9.48951C16.3411 9.6209 16.136 9.76543 15.9762 9.94331C15.5289 10.4416 15.0124 10.8327 14.3465 11.0571C14.0486 10.7226 13.7734 10.3951 13.8476 9.88065C13.9156 9.40764 13.9001 8.92453 13.9373 8.44648C14.0713 6.74952 13.728 5.07481 13.6981 3.38594C13.696 3.27578 13.6723 3.1454 13.6084 3.06151C13.2425 2.58244 12.8611 2.1155 12.5085 1.67282C12.2003 1.8123 11.9601 2.0205 11.7498 1.99422C11.3612 1.94672 11.2045 2.15088 11.0355 2.39648C10.9942 2.45611 10.987 2.54404 10.9365 2.59154C10.3242 3.17067 10.1366 3.97922 10.1758 4.69883C10.2479 6.03497 9.95208 7.40243 10.4304 8.71431C10.4489 8.76686 10.4304 8.83155 10.4345 8.89017C10.4757 9.46829 10.4252 10.0737 10.5902 10.6164C10.7159 11.0298 10.6262 11.3077 10.4665 11.6382C9.85312 11.6635 9.28617 11.7262 8.73467 11.3643C8.26874 11.0591 7.69766 10.9126 7.21523 10.6276C7.04927 10.5295 6.9266 10.483 6.73899 10.4527C6.40191 10.3981 6.08648 10.2263 5.75455 10.1283C5.55251 10.0676 5.31233 9.97565 5.13193 10.0312C4.73094 10.1545 4.35778 10.0626 3.97431 10.006C3.43313 9.92613 2.87752 9.8665 2.44251 10.2637C2.23531 10.4527 2.13532 10.7529 1.98688 11.0035C1.92709 10.9469 1.8673 10.8893 1.80752 10.8327C1.83638 11.05 1.84669 11.2714 1.89823 11.4836C2.02296 11.993 2.3755 12.2679 2.82597 12.56C3.40427 12.9339 4.03926 13.1219 4.65363 13.3736C4.8866 13.4686 5.22368 13.3251 5.45149 13.4251C6.1473 13.7304 6.85754 13.8506 7.61726 13.8446C8.06979 13.8405 8.52232 13.9851 8.97795 14.0285C9.34286 14.0639 9.71396 14.0457 10.082 14.0528C10.6891 14.0649 10.3562 14.6774 10.6396 14.8987C10.6582 14.9129 10.6417 14.9705 10.6417 14.9917C10.4551 15.0989 10.2902 15.2333 10.1005 15.298C9.36554 15.5476 8.61201 15.7457 7.88836 16.0226C7.3678 16.2217 6.85342 16.4704 6.3916 16.7766C5.94526 17.0717 5.61024 17.5336 5.15255 17.8035C4.63714 18.1067 4.34232 18.5595 3.97328 18.9819C3.64754 19.3549 3.26923 19.6773 3.0476 20.1331C2.93421 20.3656 2.77443 20.5778 2.67444 20.8153C2.57548 21.0488 2.57033 21.3288 2.44251 21.541C2.11058 22.0918 2.35798 22.5507 2.66104 22.9742C2.98266 23.4239 3.54755 23.5563 4.04132 23.6311C4.50107 23.7019 4.99999 23.5371 5.47932 23.4583C5.76074 23.4128 6.04937 23.3643 6.31326 23.2642C7.19771 22.9297 7.53376 22.0393 8.16257 21.442C8.58727 21.0387 8.7924 20.4191 9.19236 19.9815C9.52635 19.6156 9.77272 19.2265 9.9861 18.795C10.4345 17.8874 11.1468 17.1839 11.9189 16.4825C11.6684 16.2308 11.3695 16.0115 11.1798 15.7224C10.6386 14.8957 10.8262 13.9861 11.0118 13.1108C11.0674 12.849 11.3653 12.6307 11.5694 12.4074C11.787 12.1699 12.155 12.2083 12.3786 12.0809C12.992 11.7322 13.6033 11.7575 14.2496 11.809C14.9248 11.8626 15.5247 12.5307 15.5608 13.1533C15.6051 13.8729 15.5072 14.6036 15.4691 15.3576ZM13.3064 16.7665C13.1415 17.2941 12.8683 17.854 12.8095 18.4341C12.7353 19.175 12.8229 19.933 12.8631 20.6819C12.891 21.2034 13.0621 21.7391 12.9837 22.2414C12.86 23.0358 13.2301 23.6806 13.4878 24.3507C13.6043 24.6529 13.8517 24.9854 14.2898 25.0612C14.6104 25.1168 14.9124 25.2684 15.2258 25.3695C15.6227 25.4968 16.4618 25.2108 16.6793 24.8884C17.1988 24.1203 17.2184 23.2299 17.2277 22.3708C17.2349 21.7017 17.0823 21.0195 16.9184 20.3625C16.8174 19.9572 16.872 19.5125 16.4968 19.1548C15.6453 18.3422 14.9392 17.4234 14.8464 16.1682C14.3846 16.4744 14.0063 16.911 13.3064 16.7665ZM12.5848 12.659C12.5848 13.031 12.6013 13.3908 12.5807 13.7496C12.5467 14.358 12.7487 14.6946 13.3105 14.9088C13.8105 15.0989 14.0981 14.928 14.1929 14.4085C14.3795 13.3837 13.7661 12.847 12.5848 12.659Z" fill="currentColor"/>
          </svg>
        `;
    
        btn.addEventListener('click', () => {
          this.goToSlide(i);
        });
    
        container.appendChild(btn);
        bullets.push(btn);
      }
    
      this.appendChild(container);
      this.paginationBullets = bullets;
    }

    updatePagination() {
      if (this.dataset.showPagination !== 'true') return;
      if (!this.slider || !this.paginationBullets) return;
    
      const currentSlide = this.slider.track.details.rel;
    
      this.paginationBullets.forEach((bullet, index) => {
        if (index === currentSlide) {
          bullet.classList.add('is-active');
        } else {
          bullet.classList.remove('is-active');
        }
      });
    }
  
    createProgressBar() {
      if (this.dataset.showProgress !== 'true') return;

      if (this.querySelector('.ia-slider__progress')) return;

      const progressContainer = document.createElement('div');
      progressContainer.className = 'ia-slider__progress ' + this.dataset.colorSchemeUi;

      const progressBar = document.createElement('div');
      progressBar.className = 'ia-slider__progress-bar';

      const progressFill = document.createElement('div');
      progressFill.className = 'ia-slider__progress-fill';

      progressBar.appendChild(progressFill);
      progressContainer.appendChild(progressBar);
      this.appendChild(progressContainer);

      this.progressFill = progressFill;
    }

    updateProgress() {
      const totalSlides = this.slider.track.details.slides.length;
      const currentSlide = this.slider.track.details.rel;
      if (this.dataset.showProgress === 'true' && this.progressFill && this.slider) {
        
        // Only update if we have valid data
        if (totalSlides > 0) {
          const progress = ((currentSlide + 1) / totalSlides) * 100;
          this.progressFill.style.width = `${Math.min(progress, 100)}%`;
        }
      }

      if (this.dataset.loop != 'true') {
        if (this.prevButton) {
          if (currentSlide === 0) {
            this.prevButton.classList.add('ia-muted');
          } else {
            this.prevButton.classList.remove('ia-muted');
          }
        }

        if (this.nextButton) {
          console.log('currentSlide', currentSlide);
          console.log('totalSlides - 1', totalSlides - 1);
          if (currentSlide === totalSlides - 1) {
            this.nextButton.classList.add('ia-muted');
          } else {
            this.nextButton.classList.remove('ia-muted');
          }
        }
      }
    }
  
    next() {
      if (this.slider) {
        this.slider.next();
      }
    }
  
    prev() {
      if (this.slider) {
        this.slider.prev();
      }
    }
  
    goToSlide(index) {
      if (this.slider) {
        this.slider.moveToIdx(index);
      }
    }
  
    destroy() {
      if (this.slider) {
        this.slider.destroy();
        this.slider = null;
      }
    }
  }
  
  
  customElements.define('ia-slider', IASlider); 
}